<template>
  <div class="input-field w-full">
    <!-- TITLE -->
    <label
      v-if="title && !hideTitle"
      for="title"
      class="text-gray-700 text-sm font-medium flex mb-1.5"
    >
      {{ inputTitle }}
      <span v-if="rules.includes('required')" class="text-error-500 pl-1">*</span>
      <!-- TOOLTIP -->
      <div v-if="tooltipProp" class="mt-0.5 md:-mt-0.5">
        <UiOnClickOutside>
          <div
            class="flex-item ml-2 rtl:mr-2 self-center cursor-pointer"
            @click="clickToolTip"
            @mouseover="displayToolTip"
            @mouseleave="hideToolTip"
          >
            <icon
              class="icon"
              icon="infoCircle"
              color="primary-grey"
              :height="height"
              :width="width"
            />
            <div
              v-if="showToolTip"
              class="absolute -ml-4 mt-3 md:mt-2 rtl:-mr-24 md:rtl:-mr-7 z-10"
            >
              <Tooltip
                class="w-min sm:w-auto"
                :title="$t(title)"
                :text="$t(tooltipDesc)"
                direction="left"
              />
            </div>
          </div>
        </UiOnClickOutside>
      </div>
    </label>
    <!-- INPUT FIELDS -->

    <div
      class="relative text-base flex focus-within:shadow-md border rounded-lg w-full items-center hover:bg-primary-gray-50"
      :class="[
        errorbackground,
        disabled && 'cursor-not-allowed',
        isDragging && 'border-primary-purple-300 bg-primary-purple-25',
      ]"
    >
      <div class="hidden">
        <Field v-model="fieldValue" :rules="imageRules" :name="name ? title : name">
          <input
            :id="id"
            :key="inputKey"
            ref="imgUpload"
            type="file"
            :disabled="disabled"
            :multiple="!isMultiple"
            name="file"
            :accept="acceptFormat"
            @input="updateInput"
          />
        </Field>
      </div>
      <!-- FILE PLACEHOLDER -->
      <div class="rounded-lg w-full px-6 py-4">
        <label
          :for="id"
          class="cursor-pointer items-center rounded-lg flex flex-col"
          @dragover.prevent="handleDragOver"
          @dragleave="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <span
            class="flex items-center justify-center w-10 h-10 border-[6px] border-primary-gray-50 bg-primary-gray-100 rounded-full mb-3"
          >
            <icon icon="UploadFile" height="20" width="20" />
          </span>
          <div class="flex gap-1 mb-1">
            <div class="text-sm font-semibold text-primary-purple-700">Click to upload</div>
            <div class="text-sm text-primary-gray-500">or drag and drop</div>
          </div>

          <div>
            <span class="uppercase">{{ acceptTypeName }}</span>
            (max. {{ sizeLimit }} KB)
          </div>
        </label>
      </div>
      <!-- IS-VERIFIED TICK -->
      <div v-if="isVerified" class="ml-auto justify-end float-right relative">
        <div class="absolute absolute-file">
          <icon icon="circleTick" color="white" />
        </div>
      </div>
      <!-- loader -->
      <div v-if="isComponentInLoading" class="ml-auto justify-end float-right relative">
        <div class="absolute absolute-text">
          <Loader :is-background-light="true" />
        </div>
      </div>
    </div>
    <!-- error -->
    <div v-if="!noError" class="w-full info-wrapper">
      <div ref="errorContainer" class="flex justify-end h-5">
        <ErrorMessage v-slot="{ message }" :name="getErrorMessageKey">
          <small class="error-msg text-sm text-error-500 italic rtl:mt-1 ltr:-mt-0">
            <p>{{ displayError(message) }}</p>
          </small>
        </ErrorMessage>
      </div>
    </div>
    <!-- selected file -->
    <div v-if="!isFileLoading" class="w-full">
      <template v-for="(attachment, index) in currentFile" :key="index">
        <div class="mb-3 border rounded-lg flex pr-4">
          <div
            class="items-center flex gap-4 w-[70%] bg-primary-gray-50 p-4 rounded-tl-lg rounded-bl-lg"
          >
            <span
              class="flex items-center justify-center w-10 h-10 border-4 border-primary-purple-50 bg-primary-purple-100 rounded-full"
            >
              <icon icon="AttachFile" class="stroke-primary-purple-800" />
            </span>
            <div class="flex w-full">
              <div class="w-full">
                <div class="text-sm text-gray-700 font-medium truncate w-4/5">
                  <span v-if="attachment.file">{{ attachment.file?.file_name }}</span>
                  <span v-else>{{ attachment.file_name }}</span>
                </div>
                <div class="text-sm text-gray-500">
                  <span>{{ getSizeInKB(attachment.file_size) }} - KB</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-for="(attachment, index) in fieldValue" :key="index">
        <div class="mb-3 border rounded-lg flex pr-4" :class="errorClass(attachment)">
          <div
            class="items-center flex gap-4 w-[70%] bg-primary-gray-50 p-4 rounded-tl-lg rounded-bl-lg"
          >
            <span
              class="flex items-center justify-center w-10 h-10 border-4 border-primary-purple-50 bg-primary-purple-100 rounded-full"
            >
              <icon icon="AttachFile" class="stroke-primary-purple-800" />
            </span>
            <div class="flex w-full">
              <div class="w-full">
                <div class="text-sm text-gray-700 font-medium truncate w-4/5">
                  {{ attachment.name || attachment.file_name }}
                </div>
                <div class="text-sm text-gray-500">
                  {{ getSizeInKB(attachment.size || attachment.file_size) }}KB -
                  {{ currentProgress }} loaded
                </div>
              </div>
            </div>
          </div>
          <button class="cursor-pointer ml-auto pt-4" @click.prevent="removeAttachment(index)">
            <div v-if="currentProgress && currentProgress !== '100%'">
              <Loader custom-class="h-12 w-12" :is-background-light="false" type="spinner" />
            </div>
            <div v-else><icon icon="delete" /></div>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import tooltipMixin from '@/src/mixins/components/tooltip-mixin.js'
import { translatedError } from '@src/utils/generalUtil.js'
import Loader from '@components/BaseComponent/Loader.vue'
import { Field, configure, ErrorMessage } from 'vee-validate'
import icon from '@components/icons/icon.vue'
import validationMixin from '@src/mixins/components/validation-mixin.js'
import { validationConfiguration } from '@src/vee-validate/index.js'
configure(validationConfiguration)

export default {
  components: {
    ErrorMessage,
    Loader,
    Field,
    icon,
  },
  mixins: [tooltipMixin, validationMixin],
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    isFileLoading: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    currentFile: {
      type: Array,
      default: () => [],
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    acceptFormat: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: 'enterHere',
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    noError: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: 'input',
    },
    isVerified: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isComponentInLoading: {
      type: Boolean,
      default: false,
    },
    isNotTranslated: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change', 'removeFile', 'removeUploadedFile'],
  data() {
    return {
      translatedError,
      fieldValue: '',
      isErrorAvailable: false,
      inputKey: 0,
      errormessage: '',
      attachFile: [],
      sizeLimit: 0,
      maxLimit: 0,
      attachmentSize: 0,
      isDragging: false,
      currentProgress: 0,
      acceptTypeName: '',
    }
  },

  computed: {
    imageRules() {
      if (!this.rules) return ''

      const fileTypes = (this.acceptFormat || '').trim().split(/\s+/).filter(Boolean)
      const typeRule = fileTypes.length ? `type:${fileTypes.join(',')}` : ''

      return [this.rules, typeRule].filter(Boolean).join('|')
    },
    errorbackground() {
      return this.isErrorAvailable && 'border-error-300'
    },

    errorClasses() {
      return {
        'border border-error-300': this.isErrorAvailable,
        'bg-white text-menu': !this.isErrorAvailable,
      }
    },
    inputClasses() {
      return [
        this.disabled ? 'cursor-not-allowed bg-disable-color' : '',
        this.isErrorAvailable ? 'bg-bg-error' : '',
      ]
    },
    inputTitle() {
      return this.isNotTranslated ? this.title : this.$t(`placeholder.${this.title}`)
    },
    getErrorMessageKey() {
      return !this.name ? this.title : this.name
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (val) this.fieldValue = [...val]
        this.errorHandler()
      },
    },
    rules: {
      handler() {
        this.errorHandler()
      },
    },
  },
  mounted() {
    this.parseRulesAndFormats()
  },
  methods: {
    /**
     * @description function to be called on input modelValue change
     */
    errorClass(attachment) {
      let fileType = attachment.url?.split('.').pop()
      if (fileType === 'docx') {
        fileType = 'document'
      }

      if (!this.acceptFormat) {
        return {
          'border-error-500':
            this.getSizeInKB(attachment.size || attachment.file_size) >= this.sizeLimit ||
            attachment.type === '',
        }
      } else {
        return {
          'border-error-500':
            this.getSizeInKB(attachment.size || attachment.file_size) >= this.sizeLimit ||
            !this.acceptFormat.includes(attachment.type || fileType) ||
            attachment.type === '',
        }
      }
    },
    parseRulesAndFormats() {
      // Parse validation rules
      const ruleList = (this.rules || '').split('|')
      for (const rule of ruleList) {
        const [key, value] = rule.split(':')
        if (key === 'size') {
          this.sizeLimit = parseInt(value) || 0
        } else if (key === 'maxFiles') {
          this.maxLimit = parseInt(value) || 0
        }
      }

      // Parse acceptFormat and convert MIME types to user-friendly names
      if (!this.acceptFormat) return

      const mimeExtensions = this.acceptFormat
        .trim()
        .split(',')
        .map((type) => type.split('/')[1]?.trim())
        .filter(Boolean)

      const replacements = {
        'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'vnd.google-apps.document': 'docx',
        zip: 'zip',
        'x-zip-compressed': 'zip',
      }

      const acceptTypes = [...new Set(mimeExtensions.map((type) => replacements[type] || type))]

      this.acceptTypeName = acceptTypes.join(', ')
    },
    getSizeInKB(size) {
      return (size / 1000).toFixed(2)
    },
    handleDropOrInput(ev, fromInput = false) {
      this.isDragging = false
      ev.preventDefault()

      const files = fromInput ? ev.target.files : ev.dataTransfer.files
      const totalFiles = files.length + this.fieldValue.length + this.currentFile.length
      if (totalFiles <= this.maxLimit) {
        for (const file of files) {
          if (this.isFileNameUnique(file.name)) {
            this.attachFile.push(file)
            const reader = new FileReader()
            reader.onload = () => {
              this.changeimage(file)
              this.currentProgress = `${100}%`
            }
            reader.readAsDataURL(file)
            this.errormessage = ''
          } else {
            this.generateToast('error', `File name "${file.name}" already exists.`)
          }
        }
        this.$emit('update:modelValue', this.fieldValue)
        if (this.attachFile.length) {
          this.$emit('change', this.attachFile)
          this.attachFile = []
        }
      } else {
        const remaining = this.maxLimit - (this.fieldValue.length + this.currentFile.length)
        this.generateToast(
          'error',
          remaining <= 0 ? 'Limit exceeded' : `You can attach ${remaining} more file(s)`,
        )
      }
      if (fromInput) {
        ev.target.value = null
      }
    },
    generateToast(type = 'success', message = '') {
      this.$store.commit('toast/NEW', {
        message: message,
        type: type,
      })
    },

    isFileNameUnique(fileName) {
      return (
        !this.fieldValue?.some((file) => file && (file.name || file.file_name) === fileName) &&
        !this.currentFile?.some((file) => file && file.file.file_name === fileName)
      )
    },
    handleDragOver(ev) {
      ev.preventDefault()
      this.isDragging = true
    },
    handleDragLeave() {
      this.isDragging = false
    },
    handleDrop(ev) {
      this.handleDropOrInput(ev)
    },
    updateInput(ev) {
      this.handleDropOrInput(ev, true)
    },
    removeAttachment(index) {
      this.fieldValue.splice(index, 1)
      this.$emit('update:modelValue', this.fieldValue)
      this.$emit('removeFile', index)
      this.errormessage = ''
      this.inputKey += 1
    },
    removeUploaded(index) {
      this.$emit('removeUploadedFile', index)
    },
    changeimage(file) {
      this.fieldValue.push(file)
      this.$emit('update:modelValue', this.fieldValue)
    },
  },
}
</script>
